import type { IntlShape } from 'react-intl'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

export interface OfferTilePriceCaptionOptions {
  duration?: number
  durationLabel?: string
  saleUnit?: string
  unitCount?: number
  checkIn?: string
  checkOut?: string
}

/**
 * Caption-only presentation for cro-offer-tile-price-block (both treatments).
 * Callers supply the duration and unit of the price they already calculated.
 * Never parse a translated control caption or infer occupancy from a price.
 */
export function formatOfferTilePriceCaption(intl: IntlShape, {
  duration, durationLabel, saleUnit, unitCount = 1, checkIn, checkOut,
}: OfferTilePriceCaptionOptions): string | undefined {
  if (!saleUnit || saleUnit.toLowerCase() === 'total') return undefined
  const units = formatSaleUnitCount(intl, saleUnit, unitCount > 0 ? unitCount : 1)

  if (checkIn && checkOut) {
    return intl.formatMessage(
      {
        id: '8DVebA6QFtiJCB2lAblQ',
        defaultMessage: '{checkIn} - {checkOut}, {units} from',
        description: 'Alternative stay dates and priced unit count above an experimental hotel tile price',
      },
      {
        checkIn: intlDateFormatter(checkIn, INTL_DAY_MONTH_NAME_SHORT, intl.locale),
        checkOut: intlDateFormatter(checkOut, INTL_DAY_MONTH_NAME_SHORT, intl.locale),
        units,
      },
    )
  }
  if (durationLabel) {
    return intl.formatMessage(
      {
        id: 'ZhkiEbiXHSs_o5SiviyG',
        defaultMessage: '{durationLabel}, {units} from',
        description: 'Existing duration or date label and priced unit count above an experimental hotel tile price',
      },
      { durationLabel, units },
    )
  }
  if (duration && duration > 0) {
    return intl.formatMessage(
      {
        id: 'UJyybyi9b-lKXTK_eF-d',
        defaultMessage: '{duration, plural, one {# night} other {# nights}}, {units} from',
        description: 'Stay duration and priced unit count above an experimental hotel tile price',
      },
      { duration, units },
    )
  }
  return intl.formatMessage(
    {
      id: 'Kt6eI46dWNe7pVgzrw0S',
      defaultMessage: '{units} from',
      description: 'Priced unit count above an experimental hotel tile price when no duration is available',
    },
    { units },
  )
}
